import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import documentFile from 'assets/img/document-file.svg';
import dotsThreeIcon from 'assets/img/DotsThree.svg';
import delete3 from 'assets/img/delete3.svg';
import copylink from 'assets/img/copylink.svg';
import downloadIcon2 from 'assets/img/download-icon2.svg';
import APIServices from 'services/APIServices';
import { calculateTimeAgo, handleDownloadPdf, uploadFilesToApi } from 'utils/Helpers';
import { TOAST_TYPE } from 'utils/AppToast';
import boardToast from '../../utils/boardToast';
import DeleteModal from 'components/DeleteModal';
import Tippy from '@tippyjs/react';

const Attachments = ({ parentDetails, parentId, isSubtask, viewMode = false }) => {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  useEffect(() => {
    if (!parentDetails?.attachments?.length) return;
    // if (initializedRef.current) return;

    const formattedFiles = parentDetails.attachments.map((file) => ({
      id: file._id,
      name: file.name,
      preview: file.url,
      uploadedAt: file.uploadedAt,
      mimeType: file.mimeType,
      size: file.size,
      isActive: file.isActive
    }));

    setFiles(formattedFiles);
    // initializedRef.current = true;
  }, [parentDetails]);

  // ==============================
  // Handle Upload
  // ==============================
  const handleFiles = async (selectedFiles) => {
    const imageFiles = Array.from(selectedFiles);

    if (!imageFiles.length) return;

    const tempFiles = imageFiles.map((file) => ({
      tempId: crypto.randomUUID(),
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    const optimisticFiles = tempFiles.map((item) => ({
      id: item.tempId,
      name: item.name,
      preview: item.preview,
      uploadedAt: new Date(),
      uploading: true,
      isTemp: true
    }));

    setFiles((prev) => [...optimisticFiles, ...prev]);

    const tempIds = new Set(tempFiles.map((item) => item.tempId));
    const removeOptimisticFiles = () => {
      setFiles((prev) => prev.filter((file) => !tempIds.has(file.id)));
      tempFiles.forEach((item) => URL.revokeObjectURL(item.preview));
    };

    try {
      const response = await uploadFilesToApi(imageFiles, isSubtask, parentId);
      if (response?.attachments?.length > 0) {
        const normalizedFiles = response?.attachments.map((apiFile) => ({
          id: apiFile._id,
          name: apiFile.name,
          preview: apiFile.url,
          uploadedAt: new Date(),
          mimeType: apiFile.mimeType,
          size: apiFile.size,
          isActive: apiFile.isActive,
          uploading: false,
          isTemp: false
        }));

        setFiles((prev) => {
          const withoutTemp = prev.filter((file) => !tempIds.has(file.id));
          const existingIds = new Set(withoutTemp.map((f) => f.id));
          const newFilesOnly = normalizedFiles.filter((file) => !existingIds.has(file.id));

          return [...newFilesOnly, ...withoutTemp];
        });
        tempFiles.forEach((item) => URL.revokeObjectURL(item.preview));
      } else {
        throw new Error(response?.error || response?.message || 'Failed to upload attachment');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      removeOptimisticFiles();
      boardToast(error?.response?.data?.message || error?.message || 'Failed to upload attachment', TOAST_TYPE.ERROR);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleInputChange = (e) => {
    handleFiles(e.target.files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => e.preventDefault();

  // ==============================
  // Actions
  // ==============================
  const deleteAttachmentApi = async (attachmentId) => {
    const url = isSubtask
      ? `/milestone-subtask/${parentId}/attachments/${attachmentId}`
      : `/project-milestone/${parentId}/attachments/${attachmentId}`;

    return APIServices.delete(url);
  };

  const removeFile = async (attachmentId) => {
    // Optimistic UI update
    const previousFiles = files;
    setFiles((prev) => prev.filter((file) => file.id !== attachmentId));
    setOpenDropdownId(null);

    try {
      let response = await deleteAttachmentApi(attachmentId);

      // console.log(response, 'response');
      boardToast(response.message, TOAST_TYPE.ERROR);
    } catch (error) {
      console.error('Delete failed:', error);

      // Rollback UI if API fails
      setFiles(previousFiles);
    }
  };

  const downloadFile = (file) => {
    const link = document.createElement('a');
    link.href = file.preview;
    link.download = file.name;
    link.click();
    setOpenDropdownId(null);
  };

  const copyLink = async (url) => {
    await navigator.clipboard.writeText(url);
    setOpenDropdownId(null);
    boardToast('Link copied!', TOAST_TYPE.SUCCESS);
  };

  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleConfirmDelete = async () => {
    if (!selectedAttachment) return;

    setDeleteLoading(true);

    try {
      await deleteAttachmentApi(selectedAttachment.id);

      setFiles((prev) => prev.filter((file) => file.id !== selectedAttachment.id));

      boardToast('Attachment deleted successfully', TOAST_TYPE.SUCCESS);
      setShowDeleteModal(false);
      setSelectedAttachment(null);
    } catch (error) {
      console.error('Delete failed:', error);
      boardToast('Failed to delete attachment', TOAST_TYPE.ERROR);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="main_attachments">
      <div
        className="attachments_file_cover"
        onDrop={!viewMode ? handleDrop : undefined}
        onDragOver={!viewMode ? handleDragOver : undefined}
        onClick={!viewMode ? () => fileInputRef.current.click() : undefined}>
        {!viewMode && <input ref={fileInputRef} type="file" multiple hidden onChange={handleInputChange} />}

        <p>
          Drop your images here to <a>upload</a>
        </p>

        {/* {uploading && <p className="uploading_text">Uploading...</p>} */}
      </div>

      <div className="file_grid_row row">
        {files.map((item) => (
          <div key={item.id} className="col-lg-4 col-md-6 col-sm-6">
            <div className="file_card">
              <div className="file_preview">
                <a href={item.preview} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                  <img src={item.mimeType?.startsWith('image/') ? item.preview : documentFile} alt={item.name} />
                </a>
              </div>

              <div className="file_info">
                <div className="file_info_content">
                  <Tippy
                    // content={`Color: ${namer(x.color).ntc[0].name}, title: ${x?.name}`}
                    content={item.name}
                    placement="top"
                    arrow={true}>
                    <div className="file_name">{item.name}</div>
                  </Tippy>
                  <div className="file_time">{item.uploading ? 'Uploading...' : calculateTimeAgo(item.uploadedAt)}</div>
                </div>

                {!viewMode && (
                  <Dropdown align="end" show={openDropdownId === item.id} onToggle={() => {}}>
                    <Dropdown.Toggle
                      as="span"
                      className="file_menu"
                      id={`dropdown-${item.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdownId((prev) => (prev === item.id ? null : item.id));
                      }}>
                      <img src={dotsThreeIcon} alt="menu" />
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => copyLink(item.preview)}>
                        <img src={copylink} alt="copylink" className="featherdrowpdown-icon" />
                        Copy Link
                      </Dropdown.Item>

                      <Dropdown.Item onClick={() => handleDownloadPdf(item.preview, item.name)}>
                        <img src={downloadIcon2} alt="download" className="featherdrowpdown-icon" />
                        Download
                      </Dropdown.Item>

                      <Dropdown.Item
                        onClick={() => {
                          setSelectedAttachment(item);
                          setShowDeleteModal(true);
                          setOpenDropdownId(null);
                        }}
                        className="text-danger">
                        <img src={delete3} alt="delete" className="featherdrowpdown-icon" />
                        Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <DeleteModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedAttachment(null);
        }}
        handleDelete={handleConfirmDelete}
        loading={deleteLoading}
        itemName="attachment"
      />
    </div>
  );
};

export default Attachments;
